import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RESOLUTION_OPTIONS } from '../components/charts/convergence/constants';
import { resolutionLabelOf } from '../event/useEventWindow';
import {
    bucketedNow,
    NOW_BUCKET_MS,
    parseRange,
    pickResolution,
} from '../event/window';
import { assetBounds, DEFAULT_QUICK_RANGE, rangeFor } from './window';

const AVAILABLE_RESOLUTIONS: readonly number[] = RESOLUTION_OPTIONS.map(
    o => o.value
);
const RES_PARAM = 'res';

export interface AssetWindow {
    bounds: [Date, Date];
    range: [Date, Date];
    setRange: (range: [Date, Date] | null) => void;
    resolution: number;
    resolutionLabel: string;
    resolutionChoice: number | null;
    setResolutionChoice: (choice: number | null) => void;
}

function parseResolution(value: string | null): number | null {
    if (!value) return null;
    const minutes = Number(value);
    return AVAILABLE_RESOLUTIONS.includes(minutes) ? minutes : null;
}

export function useAssetWindow(): AssetWindow {
    const [searchParams, setSearchParams] = useSearchParams();
    const [nowMs, setNowMs] = useState(() => bucketedNow());

    useEffect(() => {
        const id = setInterval(() => setNowMs(bucketedNow()), NOW_BUCKET_MS);
        return () => clearInterval(id);
    }, []);

    const bounds = useMemo(() => assetBounds(nowMs), [nowMs]);

    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const urlRange = useMemo(() => parseRange(from, to), [from, to]);
    const range = useMemo(
        () => urlRange ?? rangeFor(DEFAULT_QUICK_RANGE, bounds) ?? [...bounds],
        [urlRange, bounds]
    ) as [Date, Date];

    const resolutionChoice = parseResolution(searchParams.get(RES_PARAM));

    const update = useCallback(
        (change: (params: URLSearchParams) => void) => {
            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev);
                    change(params);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setRange = useCallback(
        (next: [Date, Date] | null) =>
            update(params => {
                if (next) {
                    params.set('from', next[0].toISOString());
                    params.set('to', next[1].toISOString());
                } else {
                    params.delete('from');
                    params.delete('to');
                }
            }),
        [update]
    );

    const setResolutionChoice = useCallback(
        (choice: number | null) =>
            update(params => {
                if (choice === null) params.delete(RES_PARAM);
                else params.set(RES_PARAM, String(choice));
            }),
        [update]
    );

    const resolution = useMemo(
        () => pickResolution(range, 1, AVAILABLE_RESOLUTIONS, resolutionChoice),
        [range, resolutionChoice]
    );

    return {
        bounds,
        range,
        setRange,
        resolution,
        resolutionLabel: resolutionLabelOf(resolution),
        resolutionChoice,
        setResolutionChoice,
    };
}
